import type { ComputedRef, InjectionKey } from "vue";

import { computed, defineComponent, inject, provide } from "vue";

/**
 * 막힌 이유(`LoadingButton blockedReason`)를 어디에 보일지의 문맥(78차 UXS78-03).
 * 버튼은 `reasonPlacement="auto"`(기본)일 때 가까운 조상이 준 문맥으로 자리를 정한다 — 호출마다 값을 고르지 않는다.
 * 해석 순서는 `LoadingButton` 머리 주석을 본다. 명시값(`below` · `row` …)은 늘 문맥을 이긴다.
 */

/** 이유 글의 자리(`LoadingButton reasonPlacement` 의 명시값과 같은 어휘) */
export type ReasonPlace = "below" | "inline" | "row" | "sr" | "group" | "page";

export interface ReasonContext {
  /** 이 문맥 안 버튼의 기본 자리 */
  place?: ReasonPlace;
  /**
   * 이 문맥이 이미 보이는 글(저장 줄 왼쪽 상태 글). 버튼의 `blockedReason` 과 **같은 글**이면 이유를 다시 쓰지 않고 `group` 으로 둔다
   * (같은 문장이 한 줄 안에서 두 번 서지 않는다).
   */
  sameAs?: () => string | undefined;
  /**
   * 이 문맥이 `group` 으로 풀린 버튼의 막힘 이유를 모아 **줄 위 한 줄**로 대신 그린다(93차 C93-13 — `AppModal` 바닥 줄). 버튼은 마운트 · 이유가 바뀔 때
   * `(id, 글)` 로 알리고(글이 없으면 지움) 언마운트 때 지운다. 수집하는 문맥 안에서는 호출부가 `p.ui-btn-group-reason` 을 따로 쓰지 않는다.
   */
  collect?: (id: string, text: string | undefined) => void;
}

export const REASON_CONTEXT: InjectionKey<ReasonContext> =
  Symbol("reason-context");

/**
 * 화면 머리가 이미 말한 이유 글(읽기 역할 권한 칩 `InfoTip` 의 문장). 버튼의 `blockedReason` 이 같은 글이면 `page` —
 * 보이는 이유 줄 없이 숨은 설명 + title 만 단다.
 */
export const REASON_PAGE: InjectionKey<() => string | undefined> =
  Symbol("reason-page");

/**
 * `REASON_PAGE` 의 문장이 **역할 막힘**(읽기 전용 구성원)인가 — `LoadingButton hideWhenPageBlocked` 가 숨길지 정하는 명시 플래그(리뷰 R-DS-H1).
 * 화면 머리 문장은 역할 막힘만이 아니라 풀 수 있는 게이트(「직무분석을 먼저 확정한 뒤…」)일 수도 있다 — 후자에서 쓰기 역할의 버튼까지 사라지면 안 된다.
 */
export const REASON_PAGE_ROLE: InjectionKey<() => boolean> =
  Symbol("reason-page-role");

/** 부품 `setup` 에서 부른다 — 이 부품 아래(슬롯 내용 포함) 버튼의 기본 자리를 정한다 */
export function provideReasonContext(context: ReasonContext): void {
  provide(REASON_CONTEXT, context);
}

/**
 * 화면 `setup`(또는 그 컴포저블)에서 부른다 — 머리가 이미 말하는 이유 글을 알린다. 없으면 `undefined` 를 돌려주는 함수.
 * - `options.role`(**필수**, 82차 M16): 그 글이 **역할 막힘**(읽기 전용 구성원)일 때만 참을 돌려주는 함수. 역할 문장만 말하는 화면도 `{ role: () => !!roleNotice.value }` 로 적는다.
 *   역할 외 문장(게이트 · 생성 중 · 예시 · 낡음 · 실패)을 섞어 말하는 화면은 그 문장일 때 거짓이어야 한다(예 직무분석) — 그 문장에는 `hideWhenPageBlocked` 가 버튼을 숨기지 않는다.
 *   생략 시 늘 역할로 읽혀 owner · hr 의 버튼까지 숨던 함정을 타입으로 막는다(`LoadingButton` 의 「제공자 없으면 `page` 자리로 풀린 버튼」 옛 갈래도 같은 이유로 삭제 — 숨김 판정의 출처는 이 함수 하나).
 */
export function provideReasonPage(
  reason: () => string | undefined,
  options: { role: () => boolean },
): void {
  provide(REASON_PAGE, reason);
  provide(REASON_PAGE_ROLE, options.role);
}

/**
 * 읽기 모드 판정(82차 rg-C1 — `LoadingButton` · `RowActions` · `MenuButton` 의 `hideWhenPageBlocked` 가 한 출처를 쓴다).
 * 화면이 `provideReasonPage` 로 **역할 막힘** 문장을 말하는 동안 참. 제공자가 없는 화면은 늘 거짓 — 숨김이 일어나지 않는다.
 * 부품 `setup` 에서 부른다.
 */
export function usePageBlockedByRole(): ComputedRef<boolean> {
  const reason = inject(REASON_PAGE, undefined);
  const role = inject(REASON_PAGE_ROLE, undefined);
  return computed(() => !!reason?.() && !!role?.());
}

/**
 * 슬롯 일부에만 「이미 보이는 이유 글」 문맥을 주는 렌더리스 부품(80차 ux-b C1 · UXB80-01 ②). 자기 요소를 그리지 않는다.
 * `CardFoot` 가 `#start` 에 쓴다: 오른쪽 primary 가 막힌 이유를 이미 한 줄로 보이면, 왼쪽 버튼이 **같은 글**의 이유를 가질 때 다시 쓰지 않는다
 * (`LoadingButton reasonPlacement="auto"` 가 `group` 으로 해석 — 숨은 설명만). `ReasonScope` 는 `place` 만 주는 부품이라 따로 둔다.
 * - `same`: 이미 보이는 이유 글을 돌려주는 함수(없으면 `undefined`). 호출 때마다 읽으므로 반응형 값을 그대로 쓴다.
 * - `place`: 같은 문맥의 기본 자리(생략하면 상위 문맥을 가리지 않도록 `below`).
 */
export const ReasonSameAs = defineComponent({
  name: "ReasonSameAs",
  props: {
    same: {
      type: Function as unknown as () => () => string | undefined,
      default: undefined,
    },
    place: { type: String as () => ReasonPlace, default: "below" },
  },
  setup(props, { slots }) {
    // 바깥 문맥(저장 줄 `StickyActionBar` 의 상태 글 등)이 이미 보이는 이유도 잃지 않는다 — 가까운 쪽이 먼저, 없으면 바깥 것.
    const outer = inject(REASON_CONTEXT, undefined);
    provideReasonContext({
      get place() {
        return props.place;
      },
      sameAs: () => props.same?.() ?? outer?.sameAs?.(),
    });
    return () => slots.default?.();
  },
});
